/**
 * Backlog バグチケット（AIRCLOSET / 案件起因バグ 721649・潜在バグ 721648）の構造化カスタムフィールド・カタログ。
 *
 * Backlog の実チケットは "Templates have been moved to Custom Attributes" 方針で、本文（概要/期待/実際/原因/
 * 再現手順/発見の経路/暫定/恒久/再発防止/SQL）を **description ではなくセクション・カスタムフィールドに格納**する
 * （実チケット AIRCLOSET-140997 / 140969 / 140617 で確認）。メタ情報（緊急度・インシデントレベル・原因分類・
 * 起因リポジトリ・影響人数・Responsible Div/Group/Team/Business）も専用のリスト/数値フィールドに入る。
 *
 * このカタログを単一 source として、API（AI ドラフトのプロンプト・起票時の customField マッピング）と
 * PIL の起票モーダル（セクション入力欄・ドロップダウン）が同じフィールド ID・選択肢を参照する。
 * option id は Backlog の add_issue に `customField_<id>=<option id>`（scalar）で渡す。
 *
 * @graph-stack ops-core
 * @graph-domain Operations
 * @graph-business Backlog バグチケットのセクション本文・メタ（緊急度/発見の経路（202610新運用）/インシデント/原因分類/起因リポジトリ/影響人数/Responsible Div-Group-Team-Business）カスタムフィールドの ID・選択肢を API と PIL で共有する source。実チケットは description でなく Custom Attributes に本文を格納する
 * @graph-connects none
 */
/**
 * セクション本文カスタムフィールド（textarea）。`key` は AI ドラフト・起票入力の構造化キー、`id` は
 * Backlog フィールド ID、`guide` は AI プロンプト・モーダルのヘルプに使う記載指針（esa #4070 準拠）。
 * `required` は起票に最低限必要なセクション（概要のみ）。並び順はモーダルの表示順（esa テンプレ順）。
 * @graph-connects none
 */
export const OPS_BUG_SECTION_FIELDS = [
    {
        id: 169710,
        key: 'summary',
        label: '概要',
        required: true,
        guide: 'esa #4070 の 影響+発生範囲+発生事象 の要約。業務語で、誰に/何が起きたか/期間/件数/CS・金銭影響を先頭 3 行以内で。エンジニア用語や冒頭コードブロックは不可',
    },
    {
        id: 169705,
        key: 'expected',
        label: '期待した結果',
        required: false,
        guide: '本来どう動くべきだったか（正常時の期待挙動）',
    },
    {
        id: 169707,
        key: 'actual',
        label: '実際の結果',
        required: false,
        guide: '実際に何が起きたか（発生事象）。観測された画面・データを添える',
    },
    {
        id: 169709,
        key: 'stepsToReproduce',
        label: '再現手順',
        required: false,
        guide: '番号付きの再現手順。観測データ（スクショ / ログ抜粋 / SQL 結果）を添える',
    },
    {
        id: 169715,
        key: 'discoveryRoute',
        label: '発見の経路',
        required: false,
        guide: '検知日時 / 検知方法（CS 問い合わせ・アラート・内部監視）/ 検知が遅れた理由（検知の経緯）',
    },
    {
        id: 169698,
        key: 'cause',
        label: '原因',
        required: false,
        guide: '日本語要約（引き金・直接原因・なぜ気づけなかったか）を先に 3〜5 行。コード / permalink(SHA固定) / SQL は「### 技術詳細」に後置。不明なら仮説と次の調査ステップを書く（「調査中」で放置しない）',
    },
    {
        id: 169716,
        key: 'workaround',
        label: '暫定対応策',
        required: false,
        guide: '暫定対応（ワークアラウンド / 緩和策）。実施済みなら「YYYY-MM-DD HH:MM、誰が、何をしたか」。未実施なら予定',
    },
    {
        id: 169717,
        key: 'permanentFix',
        label: '恒久対応策',
        required: false,
        guide: '恒久対応。PR があれば「#番号 一行サマリ」。未着手なら方針',
    },
    {
        id: 183886,
        key: 'recurrencePrevention',
        label: '再発防止策',
        required: false,
        guide: 'lint ルール / アラート / モニタリング / ガイドライン更新 等の仕組みで書く。「気をつける」「レビューを厚くする」は不可',
    },
    {
        id: 169712,
        key: 'extractionSql',
        label: '抽出SQL',
        required: false,
        guide: '影響対象を抽出する SQL（調査メモにあれば）。無ければ空',
    },
];
/**
 * インシデントレベル（customField_106898、単一選択）。ops 起票は暫定判断のため「（仮）」レベルを先頭に置き、
 * AI / 人手はまず 仮 レベルを推奨する（実チケット AIRCLOSET-140617 も「レベル1（仮）」）。判断材料は
 * OPS_SEVERITY_GUIDANCE を参照。
 * @graph-connects none
 */
export const OPS_BUG_INCIDENT_LEVEL_FIELD = {
    id: 106898,
    options: [
        { id: 6, label: 'レベル1（仮）' },
        { id: 7, label: 'レベル2（仮）' },
        { id: 8, label: 'レベル3（仮）' },
        { id: 1, label: 'レベル1' },
        { id: 2, label: 'レベル2' },
        { id: 3, label: 'レベル3' },
        { id: 5, label: 'インシデントではない' },
    ],
};
/**
 * 発見の経路（202610新運用）（customField_225841、単一選択・起票必須）。2026/10 からの集計用の区分で、自由記述の
 * セクション「発見の経路」（169715）とは別物。ops 依頼から起票するバグは CS / オペタン経由で見つかったものなので
 * 既定値は `OPS_BUG_DISCOVERY_CHANNEL_FROM_OPS`（人手で変更可）。
 * @graph-connects none
 */
export const OPS_BUG_DISCOVERY_CHANNEL_FIELD = {
    id: 225841,
    options: [
        { id: 1, label: 'CS / Opetan' },
        { id: 2, label: 'E2E' },
        { id: 3, label: 'IT / ST' },
        { id: 4, label: 'Other' },
    ],
};
/**
 * ops 依頼から起票するバグの「発見の経路（202610新運用）」の既定 option id（CS / Opetan）。カタログの選択肢と
 * 一致することは bug-ticket-fields.test.ts で固定する（id の振り直しで黙って別の値を指さない）。
 * @graph-connects none
 */
export const OPS_BUG_DISCOVERY_CHANNEL_FROM_OPS = 1;
/** 原因分類（customField_174788、単一選択）。 @graph-connects none */
export const OPS_BUG_CAUSE_CLASS_FIELD = {
    id: 174788,
    options: [
        { id: 1, label: 'システム起因_仕様考慮漏れ / Spec gap' },
        { id: 2, label: 'システム起因_システムバグ / System bug' },
        { id: 5, label: 'システム起因_リリース前 / Pre-release' },
        { id: 3, label: '業務起因_ルール不足 / Rule gap' },
        { id: 4, label: '業務起因_作業起因 / Operation' },
    ],
};
/**
 * 起因リポジトリ / Caused Repository（customField_172174、複数選択だが add_issue は option id を scalar で
 * 受けるため 1 つだけ送る＝主たる起因リポジトリ）。60 リポジトリから調査メモ / PR 参照をもとに選ぶ。
 * @graph-connects none
 */
export const OPS_BUG_REPO_FIELD = {
    id: 172174,
    options: [
        { id: 1, label: 'Arthur' },
        { id: 2, label: 'ac-serverless-api' },
        { id: 3, label: 'ac-web-static' },
        { id: 4, label: 'acm-admin-api' },
        { id: 5, label: 'acm-admin-web' },
        { id: 6, label: 'acm-api' },
        { id: 7, label: 'acm-docs' },
        { id: 8, label: 'acm-infra' },
        { id: 9, label: 'acm-web' },
        { id: 55, label: 'acm-tv' },
        { id: 10, label: 'air-closet' },
        { id: 11, label: 'air-closet-api' },
        { id: 12, label: 'air-closet-batch' },
        { id: 13, label: 'air-closet-config' },
        { id: 14, label: 'air-closet-console' },
        { id: 15, label: 'air-closet-console-socket' },
        { id: 16, label: 'air-closet-console-web' },
        { id: 17, label: 'air-closet-hubot' },
        { id: 18, label: 'air-closet-migration' },
        { id: 19, label: 'air-closet-next-warehouse' },
        { id: 20, label: 'air-closet-node-batch' },
        { id: 21, label: 'air-closet-notification' },
        { id: 22, label: 'air-closet-ssr' },
        { id: 23, label: 'air-closet-static' },
        { id: 58, label: 'air-closet-static2' },
        { id: 24, label: 'air-closet-styling-api' },
        { id: 25, label: 'air-closet-styling-console' },
        { id: 26, label: 'air-closet-tsnode-batch' },
        { id: 53, label: 'air-closet-infrastructure' },
        { id: 54, label: 'air-closet-batch-infrastructure' },
        { id: 27, label: 'bridge-api' },
        { id: 28, label: 'bridge-infrastructure' },
        { id: 29, label: 'bridge-reporter' },
        { id: 30, label: 'bridge-reporter-app' },
        { id: 31, label: 'bridge-serverless' },
        { id: 32, label: 'bridge-ui-mobile' },
        { id: 33, label: 'bridge-ui-web' },
        { id: 34, label: 'cella-api' },
        { id: 35, label: 'cella-app' },
        { id: 36, label: 'cella-front' },
        { id: 37, label: 'disney-api' },
        { id: 38, label: 'disney-infrastructure' },
        { id: 39, label: 'disney-teaser' },
        { id: 40, label: 'disney-web' },
        { id: 41, label: 'disney-web-static' },
        { id: 42, label: 'payment' },
        { id: 43, label: 'payment-service' },
        { id: 44, label: 'phoenix-config' },
        { id: 45, label: 'phoenix-item' },
        { id: 46, label: 'phoenix-order' },
        { id: 47, label: 'phoenix_base' },
        { id: 59, label: 'phoenix-styling' },
        { id: 48, label: 'phoenix_infrastructure' },
        { id: 49, label: 'occasion-dress-web' },
        { id: 50, label: 'occasion-dress-backend' },
        { id: 51, label: 'occasion-dress-web-static' },
        { id: 52, label: 'occasion-dress-infrastructure' },
        { id: 56, label: 'ecosale-shopify-web' },
        { id: 57, label: 'ecosale-backend' },
        { id: 60, label: 'air-closet-mens' },
    ],
};
/**
 * 影響人数 / Number of people affected（customField_169711、数値）。起票時は scalar の数値を送る。
 * @graph-connects none
 */
export const OPS_BUG_AFFECTED_USER_COUNT_FIELD_ID = 169711;
/**
 * Responsible Div / Group / Team / Business（すべて必須の単一選択リスト）。組織アサインを表し、Backlog は
 * バグ起票時にこの 4 つを必須とする（未設定は「Responsible … は必須です」で 400）。**起因案件チケット**が
 * 分かる場合はその値をコピーするのが確実（実チケットで一致を確認）。無ければ serviceLabel / 調査メモから推定する。
 * @graph-connects none
 */
export const OPS_BUG_RESPONSIBLE_FIELDS = {
    div: {
        id: 185536,
        options: [
            { id: 1, label: 'CO' },
            { id: 2, label: 'ADO' },
            { id: 3, label: 'aC Div' },
            { id: 4, label: 'Mall Div' },
            { id: 5, label: 'PI Div' },
            { id: 6, label: 'Log Div' },
        ],
    },
    group: {
        id: 185541,
        options: [
            { id: 7, label: 'CO' },
            { id: 8, label: 'ADO' },
            { id: 1, label: 'GRG' },
            { id: 2, label: 'QAG' },
            { id: 3, label: 'M-BDG' },
            { id: 4, label: 'M-MDG' },
            { id: 5, label: 'PDG' },
            { id: 6, label: 'SCMG' },
            { id: 9, label: 'CMG' },
            { id: 10, label: 'CXG' },
        ],
    },
    team: {
        id: 185549,
        options: [
            { id: 1, label: 'PR/IR' },
            { id: 2, label: 'DST' },
            { id: 3, label: 'SFT' },
            { id: 4, label: 'CPT' },
            { id: 5, label: 'BDT' },
            { id: 6, label: 'CDT' },
            { id: 7, label: 'CRMT' },
            { id: 8, label: 'FCT' },
            { id: 9, label: 'PST' },
            { id: 10, label: 'CLT' },
            { id: 11, label: 'CST' },
            { id: 12, label: 'M-BDT' },
            { id: 13, label: 'M-DMT' },
            { id: 14, label: 'M-PST' },
            { id: 15, label: 'FLT' },
            { id: 16, label: 'RLT' },
            { id: 17, label: 'TET' },
            { id: 18, label: 'IDT' },
            { id: 19, label: 'PMOT' },
            { id: 20, label: 'SLT' },
            { id: 21, label: 'A-TET' },
            { id: 22, label: 'M-TET' },
            { id: 23, label: 'P-TET' },
            { id: 24, label: 'QDT' },
            { id: 25, label: 'SXT' },
            { id: 26, label: 'NBT' },
            { id: 27, label: 'A/M-TET' },
            { id: 28, label: 'SRET' },
            { id: 29, label: 'LPT' },
            { id: 30, label: 'SRE' },
            { id: 31, label: 'STYT' },
        ],
    },
    business: {
        id: 185550,
        options: [
            { id: 1, label: 'エアクロ' },
            { id: 2, label: 'エアクロモール' },
            { id: 9, label: 'Spot Rental' },
            { id: 10, label: 'エアクロメンズ' },
            { id: 4, label: 'AC PORT' },
            { id: 5, label: '全社インフラ' },
            { id: 6, label: '社長室' },
            { id: 7, label: '経営管理室' },
            { id: 8, label: 'Other' },
        ],
    },
};
//# sourceMappingURL=bug-ticket-fields.js.map